import { Fragment, useEffect, useMemo, useState } from 'react';
// @mui
import {
  Box,
  Card,
  Stack,
  Table,
  Button,
  Collapse,
  MenuItem,
  TableRow,
  TextField,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  IconButton,
  TableContainer,
  CircularProgress,
} from '@mui/material';
// components
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
// utils
import axiosInstance from '../../utils/axios';
import { APIS } from '../../utils/serviceUrls';
import fmtDuration from '../../utils/formatDuration';
import { fDateTimeSuffix } from '../../utils/formatTime';
import { downloadCsv, slugForFile, CsvCell } from '../../utils/csv';

// Full date + time a student submitted, e.g. "03/08/2026 11:24 AM". Empty when
// the attempt carries no timestamp (legacy rows).
function submittedAt(iso?: string): string {
  if (!iso) return '';
  try {
    return fDateTimeSuffix(new Date(iso));
  } catch {
    return String(iso).slice(0, 10);
  }
}

// ---------------------------------------------------------------------------
// Student x question grid for ONE quiz.
//
// Each row is a student's BEST attempt (see the backend note: "most recent" is
// unreliable when attempts share a timestamp, and an abandoned blank retry
// would render as "answered nothing"). Expanding a row lists every question
// with what they scored on it.
// ---------------------------------------------------------------------------

type AnswerCell = {
  questionIndex: number;
  questionTitle: string;
  correct: boolean;
  answered: boolean;
  earnedPoints: number;
  maxPoints: number;
};

type StudentRow = {
  userId: string;
  studentName?: string;
  studentEmail?: string;
  batchName?: string;
  attempts: number;
  bestPercentage: number;
  bestScore: number;
  bestMaxScore: number;
  bestAttemptNumber: number;
  bestDurationSeconds: number;
  lastAt?: string;
  passed: boolean;
  answers: AnswerCell[];
};

// One cell in the grid: correct / wrong / skipped.
function Mark({ cell }: { cell?: AnswerCell }) {
  if (!cell || !cell.answered) {
    return (
      <Typography component="span" variant="body2" sx={{ color: 'text.disabled' }} title="Skipped">
        –
      </Typography>
    );
  }
  return cell.correct ? (
    <Iconify icon="eva:checkmark-circle-2-fill" sx={{ color: 'success.main', width: 18, height: 18 }} />
  ) : (
    <Iconify icon="eva:close-circle-fill" sx={{ color: 'error.main', width: 18, height: 18 }} />
  );
}

export default function StudentAnswers({
  quizId,
  courseId,
  eventId,
  batchId,
  quizTitle,
  batchLabel,
}: {
  quizId: string;
  courseId?: string;
  eventId?: string;
  batchId?: string;
  // Optional context for the export filename/header (the analysis page knows
  // the chosen quiz + batch; the grid itself only receives ids).
  quizTitle?: string;
  batchLabel?: string;
}) {
  const [rows, setRows] = useState<StudentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [filter, setFilter] = useState<'all' | 'passed' | 'failed'>('all');
  // Sort by submission time (when they finished the exam — `lastAt`) or by the
  // time taken in the quiz (the attempt's duration).
  const [sortBy, setSortBy] = useState<
    | 'default'
    | 'submitted_desc'
    | 'submitted_asc'
    | 'duration_desc'
    | 'duration_asc'
    | 'score_time'
  >('default');

  useEffect(() => {
    if (!quizId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    axiosInstance
      .get(APIS.QUIZ_ATTEMPTS.STUDENT_ANSWERS, {
        params: {
          quizId,
          courseId: courseId || undefined,
          eventId: eventId || undefined,
          batchId: batchId || undefined,
        },
      })
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data ?? res.data;
        setRows(Array.isArray(d?.students) ? d.students : []);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message || 'Failed to load student answers');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [quizId, courseId, eventId, batchId]);

  // Column count = the widest answer set, so a student served fewer questions
  // (randomised subset) still lines up.
  const questionCount = useMemo(
    () =>
      rows.reduce(
        (m, r) => Math.max(m, ...(r.answers || []).map((a) => a.questionIndex + 1), 0),
        0
      ),
    [rows]
  );

  const visible = useMemo(() => {
    const filtered = rows.filter((r) =>
      filter === 'all' ? true : filter === 'passed' ? r.passed : !r.passed
    );
    if (sortBy === 'default') return filtered;
    const ts = (r: StudentRow) => (r.lastAt ? new Date(r.lastAt).getTime() : 0);
    const dur = (r: StudentRow) => r.bestDurationSeconds || 0;
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'submitted_desc':
          return ts(b) - ts(a);
        case 'submitted_asc':
          return ts(a) - ts(b);
        case 'duration_desc':
          return dur(b) - dur(a);
        case 'duration_asc':
          return dur(a) - dur(b);
        case 'score_time':
          // Best performance: highest score first; ties broken by the fastest
          // time, then by the oldest submission (earliest to finish).
          return (
            b.bestPercentage - a.bestPercentage ||
            dur(a) - dur(b) ||
            ts(a) - ts(b)
          );
        default:
          return 0;
      }
    });
  }, [rows, filter, sortBy]);

  // Per-question totals across students, shown as a footer row.
  const perQuestionCorrect = useMemo(() => {
    const acc = new Array(questionCount).fill(0);
    const tot = new Array(questionCount).fill(0);
    rows.forEach((r) =>
      (r.answers || []).forEach((a) => {
        if (a.questionIndex < questionCount && a.answered) {
          tot[a.questionIndex] += 1;
          if (a.correct) acc[a.questionIndex] += 1;
        }
      })
    );
    return acc.map((n, i) => (tot[i] ? Math.round((n / tot[i]) * 100) : null));
  }, [rows, questionCount]);

  // Export #1 — every student who took THIS quiz, with their score + when they
  // submitted, plus a per-question correct/wrong/skipped breakdown. Exports the
  // full roster (not just the on-screen filter) so it's a complete record.
  const handleExport = () => {
    if (!rows.length) return;
    const qCols = Array.from({ length: questionCount }, (_, i) => `Q${i + 1}`);
    const header: CsvCell[] = [
      'Student',
      'Email',
      'Batch',
      'Attempts',
      'Best %',
      'Best score',
      'Max score',
      'Result',
      'Submitted',
      'Time taken',
      ...qCols,
    ];
    const ordered = [...rows].sort(
      (a, b) => b.bestPercentage - a.bestPercentage || (a.studentName || '').localeCompare(b.studentName || '')
    );
    const body: CsvCell[][] = ordered.map((r) => {
      const byIndex = new Map((r.answers || []).map((a) => [a.questionIndex, a]));
      const cells: CsvCell[] = [
        r.studentName || '',
        r.studentEmail || '',
        r.batchName || batchLabel || '',
        r.attempts,
        r.bestPercentage,
        r.bestScore,
        r.bestMaxScore,
        r.passed ? 'Passed' : 'Failed',
        submittedAt(r.lastAt),
        r.bestDurationSeconds ? fmtDuration(r.bestDurationSeconds) : '',
      ];
      for (let i = 0; i < questionCount; i += 1) {
        const c = byIndex.get(i);
        cells.push(!c || !c.answered ? 'Skipped' : c.correct ? 'Correct' : 'Wrong');
      }
      return cells;
    });
    const name = `quiz-${slugForFile(quizTitle || quizId)}${batchLabel ? `-${slugForFile(batchLabel)}` : ''}-students`;
    downloadCsv(name, [header, ...body]);
  };

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={22} />
      </Stack>
    );
  }
  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ py: 2 }}>
        {error}
      </Typography>
    );
  }
  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No students have attempted this quiz in the current scope yet.
      </Typography>
    );
  }

  return (
    <Box>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Showing {visible.length} of {rows.length} student{rows.length === 1 ? '' : 's'}. Each row
          is the student&apos;s <b>best</b> attempt. ✓ correct · ✗ wrong · – skipped. Click a row for
          the full breakdown.
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
          <TextField
            select
            size="small"
            label="Show"
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'all' | 'passed' | 'failed')}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All students</MenuItem>
            <MenuItem value="passed">Passed only</MenuItem>
            <MenuItem value="failed">Not passed</MenuItem>
          </TextField>

          {/* Sort by submission time (when they finished) or time taken. */}
          <TextField
            select
            size="small"
            label="Sort by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="default">Default order</MenuItem>
            <MenuItem value="score_time">Best — highest score, then fastest</MenuItem>
            <MenuItem value="submitted_desc">Submission time — newest first</MenuItem>
            <MenuItem value="submitted_asc">Submission time — oldest first</MenuItem>
            <MenuItem value="duration_desc">Time taken — longest first</MenuItem>
            <MenuItem value="duration_asc">Time taken — shortest first</MenuItem>
          </TextField>

          <Box sx={{ flexGrow: 1 }} />

          {/* Export #1: every student who took this quiz + their scores. */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<Iconify icon="eva:download-outline" />}
            onClick={handleExport}
            disabled={!rows.length}
          >
            Export CSV
          </Button>
        </Stack>
      </Stack>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 200 }}>Student</TableCell>
              <TableCell align="center">Score</TableCell>
              <TableCell align="center">Attempts</TableCell>
              <TableCell align="center" sx={{ minWidth: 140 }}>
                Submitted
              </TableCell>
              {Array.from({ length: questionCount }, (_, i) => (
                <TableCell key={i} align="center" sx={{ px: 0.75 }}>
                  Q{i + 1}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((r) => {
              const isOpen = !!open[r.userId];
              const byIndex = new Map((r.answers || []).map((a) => [a.questionIndex, a]));
              return (
                <Fragment key={r.userId}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setOpen((p) => ({ ...p, [r.userId]: !p[r.userId] }))}
                  >
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="subtitle2" noWrap>
                          {r.studentName || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {r.studentEmail || ''}
                          {r.batchName ? ` · ${r.batchName}` : ''}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Label color={r.passed ? 'success' : 'error'}>{r.bestPercentage}%</Label>
                    </TableCell>
                    <TableCell align="center">{r.attempts}</TableCell>
                    <TableCell align="center">
                      <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
                        {submittedAt(r.lastAt) || '—'}
                      </Typography>
                    </TableCell>
                    {Array.from({ length: questionCount }, (_, i) => (
                      <TableCell key={i} align="center" sx={{ px: 0.75 }}>
                        <Mark cell={byIndex.get(i)} />
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      <IconButton size="small">
                        <Iconify
                          icon={isOpen ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={questionCount + 5}
                      sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}
                    >
                      <Collapse in={isOpen} unmountOnExit>
                        <Box sx={{ py: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Best attempt #{r.bestAttemptNumber} · {r.bestScore}/{r.bestMaxScore}{' '}
                            points
                            {r.bestDurationSeconds
                              ? ` · took ${fmtDuration(r.bestDurationSeconds)}`
                              : ''}
                            {r.lastAt
                              ? ` · last activity ${String(r.lastAt).slice(0, 10)}`
                              : ''}
                          </Typography>
                          <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                            {(r.answers || []).map((a) => (
                              <Stack
                                key={a.questionIndex}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Mark cell={a} />
                                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                  {a.questionIndex + 1}. {a.questionTitle}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {a.earnedPoints}/{a.maxPoints}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}

            {/* Class accuracy per question — the column-wise read of the grid */}
            <TableRow sx={{ bgcolor: 'background.neutral' }}>
              <TableCell>
                <Typography variant="subtitle2">Class accuracy</Typography>
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
              {perQuestionCorrect.map((pct, i) => (
                <TableCell key={i} align="center" sx={{ px: 0.75 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color:
                        pct === null
                          ? 'text.disabled'
                          : pct >= 80
                          ? 'success.main'
                          : pct >= 50
                          ? 'warning.main'
                          : 'error.main',
                    }}
                  >
                    {pct === null ? '–' : `${pct}%`}
                  </Typography>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
